import * as tus from 'tus-js-client'
import { authClient } from '@yoreflix/core/api/authClient'
import { tusEndpoint } from './uploadClient'

/**
 * The resumable transport, client side (U2/U3).
 *
 * ★★ WHY A WRAPPER AND NOT tus-js-client DIRECTLY. Our API is guarded by the four-token scheme,
 * and one of those tokens ROTATES ON EVERY REQUEST. A multi-gigabyte upload is hundreds of
 * requests, so the auth lifecycle has to be threaded through every one of them. Everything below
 * exists for that.
 *
 * ⚠⚠ THE ROTATION FORBIDS PARALLEL CHUNKS. `RequestToken` is single-use and rotates per request
 * (AuthService: CAS + a 10s grace window). Two chunks in flight together race the rotation and the
 * straggler 428s. So `parallelUploads: 1` is NOT a performance oversight — it is a correctness
 * requirement of our auth scheme, and raising it will produce intermittent, load-dependent 428
 * storms that look like a server fault. Throughput comes from chunk SIZE, not concurrency.
 *
 * ⚠ CSRF is held HERE, not pushed back into authClient. authClient has no setter (deliberately —
 * it serializes its own calls to avoid exactly this race), so after an upload its cached CSRF is
 * stale. That is harmless: its next call 428s once and its own single-flight refresh fixes it
 * transparently. Adding a setter to shared core to save one refresh would be a bad trade.
 */

/** 8 MB: big enough to keep throughput up on a good line, small enough that a retry is cheap on a bad one. */
export const CHUNK_SIZE = 8 * 1024 * 1024

/** Backoff for a flaky domestic connection — the case this portal exists for. */
const RETRY_DELAYS = [0, 1000, 3000, 5000, 10000, 20000, 30000]

const CSRF_HEADER = 'X-Yoreflix-CSRF'

/** The rotating CSRF, seeded from authClient and advanced by every tus response. */
let currentCsrf: string | null = null

function csrf(): string {
  if (currentCsrf === null) currentCsrf = authClient.csrfToken()
  return currentCsrf ?? ''
}

export interface UploadHandle {
  /** Pause without losing progress — the bytes already accepted stay on the server. */
  abort(): Promise<void>
  /** Resume a paused/failed upload from wherever the server says it got to. */
  start(): void
}

/**
 * ★★ THE AUTH EVERY TUS REQUEST NEEDS, IN ONE PLACE.
 *
 * Both an in-flight upload and the standalone Termination behind "Cancel upload" go through these,
 * because a request that forgets any one of them fails in a way that reads as a server fault:
 * without the cookies it is anonymous (401), without the rotating CSRF it 428s forever, and a
 * cached offset response would resume from the wrong place.
 */
function authorizeRequest(req: tus.HttpRequest): void {
  // ⚠⚠ COOKIES. tus-js-client v4 has NO `withCredentials` option — it is set on the underlying
  // XHR. Without this the four session cookies never leave the browser and EVERY request 401s,
  // which reads as "the upload endpoint is broken" rather than "the request was anonymous".
  const xhr = req.getUnderlyingObject() as XMLHttpRequest | undefined
  if (xhr && 'withCredentials' in xhr) xhr.withCredentials = true

  req.setHeader(CSRF_HEADER, csrf())
  // ⚠ The offset response cannot carry `no-store` (GridPane's nginx overwrites it site-wide), so
  // the CLIENT forces end-to-end revalidation instead. A cached offset would resume from the wrong
  // place and corrupt the file silently.
  req.setHeader('Cache-Control', 'no-cache')
}

/** The server rotates the CSRF on every response; carry it to the next request. */
function absorbRotatedCsrf(_req: tus.HttpRequest, res: tus.HttpResponse): void {
  const rotated = res.getHeader(CSRF_HEADER) || res.getHeader(CSRF_HEADER.toLowerCase())
  if (rotated) currentCsrf = rotated
}

/**
 * 428 (stale token) and 409 (replay) are the auth transport's "refresh and try again", NOT
 * business errors — treating them as fatal would abandon a perfectly good multi-GB upload over a
 * token race. Everything else falls back to tus's own retry policy.
 */
function retryOnAuthChurn(err: Error, retryAttempt: number): boolean {
  const status = statusOf(err)
  if (status === 428 || status === 409) {
    void authClient.refresh().then(() => { currentCsrf = authClient.csrfToken() }).catch(() => {})
    return true
  }
  // A 4xx that is not ours is a real refusal (403 no permission, 404 wrong project) — stop.
  if (status >= 400 && status < 500 && status !== 423 && status !== 429) return false
  return retryAttempt < RETRY_DELAYS.length - 1
}

/** The HTTP status behind a tus error, or 0 when it never reached the server. */
function statusOf(err: unknown): number {
  return (err as { originalResponse?: { getStatus(): number } }).originalResponse?.getStatus?.() ?? 0
}

/**
 * What happened when we tried to throw an upload away.
 *
 * ★ Three outcomes rather than a boolean, because they mean opposite things to the provider:
 * `gone` — terminated (or already absent); nothing is left on our disk.
 * `complete` — the upload had actually FINISHED and only the browser missed the last response.
 *   Nothing to cancel and nothing wrong: B7 says those bytes are ours now, and the server refuses
 *   a Termination on a completed upload for exactly that reason.
 * `failed` — we never got an answer. The record must be KEPT so they can try again.
 */
export type TerminateOutcome = 'gone' | 'complete' | 'failed'

/**
 * Throw away an interrupted upload, given only its stored URL.
 *
 * ★ There is no `tus.Upload` to abort here: after a page reload the `File` is gone and all that
 * survives is the upload URL, so this is tus's static Termination request — decorated with exactly
 * the same auth as every other call above.
 */
export async function terminateUpload(url: string): Promise<TerminateOutcome> {
  try {
    await tus.Upload.terminate(url, {
      retryDelays: RETRY_DELAYS,
      onBeforeRequest: authorizeRequest,
      onAfterResponse: absorbRotatedCsrf,
      onShouldRetry: retryOnAuthChurn,
    })
    return 'gone'
  } catch (err) {
    const status = statusOf(err)
    // Already not there — the same end state the provider asked for, so not an error to them.
    if (status === 404 || status === 410) return 'gone'
    if (status === 403) return 'complete'
    return 'failed'
  }
}

export interface StartUploadOptions {
  file: File
  projectKey: string
  /** Fires often — throttle in the UI, not here. */
  onProgress(sent: number, total: number): void
  onSuccess(fileKey: string): void
  onError(error: Error): void
  /** The tus upload URL, as soon as it exists — persist it to survive a reload. */
  onUrl?(url: string): void
  /** A previously stored upload URL, to resume rather than restart. */
  resumeUrl?: string
}

/**
 * Begin (or resume) one file's upload.
 *
 * ★ Resume-after-refresh works because of two independent facts: the SERVER knows the offset (it
 * is the byte length on disk), and tus-js-client fingerprints the file so a stored upload URL can
 * be matched back to it. What the browser cannot do is re-read the bytes after a reload without
 * either the user re-picking the file or a File System Access handle — see ./resume.ts.
 */
export function startUpload(opts: StartUploadOptions): UploadHandle {
  const upload = new tus.Upload(opts.file, {
    endpoint: tusEndpoint(),
    uploadUrl: opts.resumeUrl,
    chunkSize: CHUNK_SIZE,
    retryDelays: RETRY_DELAYS,
    // ⚠ Correctness, not throughput — see the note at the top of this file.
    parallelUploads: 1,
    removeFingerprintOnSuccess: true,
    metadata: { filename: opts.file.name, filetype: opts.file.type },

    // Our own header, not part of tus: it tells the server which project these bytes belong to,
    // and the server then checks that project belongs to the caller's organization.
    headers: { 'Upload-Project': opts.projectKey },

    // ★ Shared with terminateUpload() — see the note above each. One implementation, so a change
    // to the auth dance cannot fix the upload path and quietly break Cancel.
    onBeforeRequest: authorizeRequest,
    onAfterResponse: absorbRotatedCsrf,
    onShouldRetry: retryOnAuthChurn,

    onProgress(sent, total) {
      opts.onProgress(sent, total)
    },

    onSuccess() {
      // The file key is the last segment of the upload URL the server minted.
      const key = (upload.url ?? '').split('/').filter(Boolean).pop() ?? ''
      opts.onSuccess(key)
    },

    onError(error) {
      opts.onError(error instanceof Error ? error : new Error(String(error)))
    },
  })

  if (opts.onUrl) {
    // tus sets `url` once creation has happened; poll briefly rather than patching the library.
    const seen = setInterval(() => {
      if (upload.url) {
        opts.onUrl?.(upload.url)
        clearInterval(seen)
      }
    }, 250)
    setTimeout(() => clearInterval(seen), 60_000)
  }

  upload.start()

  return {
    start: () => upload.start(),
    abort: async () => {
      // `true` terminates the upload on the server too (tus Termination) — a cancelled transfer
      // must not leave bytes lying around waiting for a collection that will never come.
      await upload.abort(true)
    },
  }
}
